// The overview's arithmetic (UI-5): the morning question — what went out, what
// is running, what is red — added up across the pools. Apart from the page so
// it runs on Node in tests/admin.

import type { AlertRow, WarmupPool } from "@/lib/api";
import { count, share } from "@/components/emc/format";
import type { Tone } from "@/components/emc/status";

export interface DaySum {
  sent: number;
  bounced: number;
  complained: number;
  /** How many pools the day's figures came from; 0 is "nothing known". */
  pools: number;
  /** Some pool's figures were recomputed before the day ended. */
  partial: boolean;
}

/** Yesterday, all pools: the recomputed figures of the pools that have them.
 * A pool with no figures is left out of the sum rather than counted as zero,
 * and `pools` says how many were in it. */
export function yesterdaySum(pools: WarmupPool[]): DaySum {
  const known = pools.filter((pool) => pool.yesterday.rates !== null);
  return {
    sent: known.reduce((sum, pool) => sum + (pool.yesterday.rates?.sent ?? 0), 0),
    bounced: known.reduce((sum, pool) => sum + (pool.yesterday.rates?.bounced ?? 0), 0),
    complained: known.reduce((sum, pool) => sum + (pool.yesterday.rates?.complained ?? 0), 0),
    pools: known.length,
    partial: known.some((pool) => pool.yesterday.partial),
  };
}

/** "bounce 0,6% · panasz 0,02%" — or why there is nothing to say. */
export function daySumLine(day: DaySum): string {
  if (day.pools === 0) return "tegnapról nincs újraszámolt adat";
  if (day.sent === 0) return "tegnap nem ment ki levél";
  const parts = [
    `bounce ${share(day.bounced, day.sent) ?? "—"}`,
    `panasz ${share(day.complained, day.sent) ?? "—"}`,
  ];
  if (day.partial) parts.push("részleges");
  return parts.join(" · ");
}

/** The pools something is holding back: stopped first, then slowed. */
export function heldPools(pools: WarmupPool[]): { stopped: WarmupPool[]; slowed: WarmupPool[] } {
  return {
    stopped: pools.filter((pool) => pool.is_paused || pool.watch.state === "paused"),
    slowed: pools.filter((pool) => !pool.is_paused && pool.watch.state === "throttled"),
  };
}

export interface AlertCount {
  open: number;
  critical: number;
  tone: Tone;
  line: string;
}

/** The last day's alerts: how many nobody has acknowledged, how many critical. */
export function alertCount(alerts: AlertRow[]): AlertCount {
  const open = alerts.filter((alert) => alert.acknowledged_at === null);
  const critical = open.filter((alert) => alert.level === "critical").length;
  return {
    open: open.length,
    critical,
    tone: critical > 0 ? "danger" : open.length > 0 ? "warn" : "muted",
    line:
      open.length === 0
        ? alerts.length === 0
          ? "nem volt riasztás"
          : "mind nyugtázva"
        : `${critical} kritikus, ${open.length - critical} egyéb nyugtázatlan`,
  };
}

/** One pool's state in words, for its line on the overview. */
export function poolLine(pool: WarmupPool): { text: string; tone: Tone } {
  if (pool.is_paused || pool.watch.state === "paused") {
    return {
      text: `áll: ${pool.paused_reason || pool.watch.reason || "nincs indok a soron"}`,
      tone: "danger",
    };
  }
  if (pool.warmup_state === "cold") return { text: "hideg: még nem indult", tone: "muted" };
  const sent = pool.today.rates?.sent ?? 0;
  const ofBudget = share(sent, pool.budget_today);
  const slowed = pool.watch.state === "throttled" ? " · lassítva" : "";
  return {
    text: `${count(sent)} / ${count(pool.budget_today)}${ofBudget ? ` · ${ofBudget}` : ""}${slowed}`,
    tone: slowed ? "warn" : "muted",
  };
}
